import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { Seo } from "../components/Seo";
import { Eyebrow, Reveal } from "../components/ui-bits";
import { CTABand } from "../components/PageSections";
import { FAQS } from "../data/site";

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

function FaqItem({ faq, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <Reveal delay={index * 0.04}>
      <div className="border-b border-gold/30">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          data-testid={`faq-question-${index}`}
          className="flex w-full items-center justify-between gap-6 py-6 text-left"
        >
          <span className="font-serif text-xl font-medium text-plum sm:text-2xl">{faq.q}</span>
          <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.3 }}>
            <ChevronDown className="h-5 w-5 shrink-0 text-plum" aria-hidden="true" />
          </motion.span>
        </button>
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
            >
              <p className="max-w-3xl pb-6 text-[16px] leading-relaxed text-charcoal/85">{faq.a}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Reveal>
  );
}

export default function Faq() {
  return (
    <>
      <Seo
        title="FAQ | JIF – Jack Iannotti Flowers, Coventry RI"
        description="Answers about ordering by phone, same-day delivery, Kent Hospital deliveries, funeral flowers, and our satisfaction guarantee."
        jsonLd={faqJsonLd}
      />
      <section className="mx-auto max-w-4xl px-5 pb-24 pt-40 sm:px-8">
        <Reveal>
          <Eyebrow>Good Questions</Eyebrow>
          <h1 className="mt-5 font-serif text-4xl font-medium leading-[1.08] tracking-tight text-plum sm:text-5xl lg:text-6xl">
            Frequently Asked Questions
          </h1>
          <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-charcoal/85 sm:text-[18px]">
            Everything people usually ask when they call. If your question isn't here, the fastest answer is the
            phone.
          </p>
        </Reveal>
        <div className="mt-14">
          {FAQS.map((f, i) => (
            <FaqItem key={f.q} faq={f} index={i} />
          ))}
        </div>
      </section>
      <CTABand title="Still wondering? Just call" />
    </>
  );
}
